import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API_BASE_URL } from "../../config/api";
import { Select, Alert } from "antd";
import axios from "axios";
import { toast } from "react-toastify";
import DashboardLayout from "../layout";
import Header from "../header/Header";
import CustomButton from "../shared/common/customButton";
import CountrySelectInput from "../shared/countrySelectInput";
import MultiCountrySelectInput from "../shared/countrySelectInput/MultiCountrySelectInput";
import ServiceTempIOR from "../forms/servicesTemp/tempIOR";
import { FaArrowLeft } from "react-icons/fa6";
import TestPopup from '../forms/TestPopup';

const { Option } = Select;

const EditIORForm = () => {
  const { estimateId } = useParams();
  const navigate = useNavigate();

  const [values, setValues] = useState({
    loadCountry: [],
    unloadCountry: "",
    airfreightCurrency: "USD",
    afcWeightUnit: "kg",
    selectedTemplate: {
      name: "",
      services: []
    },
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [templateSelected, setTemplateSelected] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [savedServices, setSavedServices] = useState([]);
  const [estimationExists, setEstimationExists] = useState(false);
  const [matchingCountries, setMatchingCountries] = useState([]);
  const [isTestPopupVisible, setIsTestPopupVisible] = useState(false);

  useEffect(() => {
    const fetchEstimate = async () => {
      try {
        const response = await api.get(`${API_BASE_URL}/estimates/${estimateId}`);
        setValues(response.data);
        setSavedServices(response.data.selectedTemplate.services);
        setTemplateSelected(true);
      } catch (error) {
        console.error("Error fetching estimate:", error);
        toast.error("Failed to fetch estimate data");
      }
    };

    fetchEstimate();
  }, [estimateId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
    // Reset estimation exists when form changes
    setEstimationExists(false);
    setMatchingCountries([]);
  };

  const handleSelectChange = (name, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
    // Reset estimation exists when form changes
    setEstimationExists(false);
    setMatchingCountries([]);
  };

  const checkEstimationExists = async () => {
    if (
      values.loadCountry.length > 0 &&
      values.unloadCountry &&
      values.airfreightCurrency &&
      values.afcWeightUnit &&
      values.selectedTemplate.name
    ) {
      try {
        const response = await api.post(`${API_BASE_URL}/estimates/check-exists`, {
          loadCountry: values.loadCountry,
          unloadCountry: values.unloadCountry,
          airfreightCurrency: values.airfreightCurrency,
          afcWeightUnit: values.afcWeightUnit,
          templateName: values.selectedTemplate.name,
          excludeId: estimateId
        });
        setEstimationExists(response.data.exists);
        setMatchingCountries(response.data.matchingCountries || []);
      } catch (error) {
        console.error("Error checking estimation existence:", error);
      }
    } else {
      // Reset estimation exists if form is incomplete
      setEstimationExists(false);
      setMatchingCountries([]);
    }
  };

  const validateForm = () => {
    let formErrors = {};
    let isValid = true;

    if (!values.afcWeightUnit) {
      formErrors.afcWeightUnit = "Weight Unit is required";
      isValid = false;
    }

    if (!values.airfreightCurrency) {
      formErrors.airfreightCurrency = "Airfreight Currency is required";
      isValid = false;
    }

    if (!values.loadCountry || values.loadCountry.length === 0) {
      formErrors.loadCountry = "Load Country is required";
      isValid = false;
    }

    if (!values.unloadCountry) {
      formErrors.unloadCountry = "Unload Country is required";
      isValid = false;
    }

    if (!values.selectedTemplate || !values.selectedTemplate.name) {
      formErrors.selectedTemplate = "A template must be selected";
      isValid = false;
    }

    if (!values.selectedTemplate.services || values.selectedTemplate.services.length === 0) {
      formErrors.services = "At least one service is required";
      isValid = false;
    } else {
      const invalidServices = values.selectedTemplate.services.filter(
        service => !service.description || service.values.length === 0
      );
      if (invalidServices.length > 0) {
        formErrors.services = "All services must have a description and at least one value";
        isValid = false;
      }
    }

    setValidationErrors(formErrors);
    setIsFormValid(isValid && !estimationExists);
    return isValid;
  };

  useEffect(() => {
    validateForm();
    // Add a small delay to avoid rapid API calls
    const timeoutId = setTimeout(() => {
      checkEstimationExists();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [values, savedServices]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm() || estimationExists) {
      toast.error(estimationExists ? "Estimation already exists for the selected criteria" : "Please fill all mandatory fields and ensure all services are properly filled");
      return;
    }

    const formattedData = {
      ...values,
      selectedTemplate: {
        name: values.selectedTemplate.name,
        services: savedServices
      },
    };

    try {
      const response = await api.put(`${API_BASE_URL}/estimates/${estimateId}`, formattedData);

      if (response.status === 200) {
        toast.success("Estimate updated successfully");
        navigate("/settings");
      } else {
        toast.error("Error updating estimate");
      }
    } catch (error) {
      console.error("Unexpected error", error);
      if (error.response && error.response.data && error.response.data.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("An unexpected error occurred");
      }
    }
  };

  const onBack = () => {
    navigate("/settings");
  };

  const handleTestPopupClose = () => {
    setIsTestPopupVisible(false);
  };

  const handleTestClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsTestPopupVisible(true);
  };

  return (
    <DashboardLayout>
      <div className="w-full">
        <Header title="Edit IOR" />
        <div className="p-4 bg-cus-gray">
          <div className="mb-3">
            <button
              type="button"
              onClick={onBack}
              className="flex rounded-[5px] items-center space-x-4 py-2 px-4 text-white btnBg"
            >
              <FaArrowLeft className="mr-2" /> Back
            </button>
          </div>
          <form
            onSubmit={handleSubmit}
            className="p-4 mx-auto bg-white border rounded-md"
          >
            {estimationExists && (
              <Alert
                message="Estimation already exists"
                description={`An estimation already exists for the following load countries: ${matchingCountries.join(', ')}. Please modify your selection.`}
                type="warning"
                showIcon
                className="mb-4"
              />
            )}
            <div className="mx-[30px] flex flex-wrap md:flex-nowrap gap-3 mt-4">
              <div className="w-full md:w-1/3">
                <label
                  className="w-full text-sm font-normal"
                  htmlFor="loadCountry"
                >
                  Load Country <span className="text-red-500">*</span>
                </label>
                <MultiCountrySelectInput
                  name="loadCountry"
                  id="loadCountry"
                  placeholder="Select Countries"
                  value={values.loadCountry}
                  onChange={handleChange}
                  error={validationErrors.loadCountry}
                />
              </div>
              <div className="w-full md:w-1/3">
                <label
                  className="w-full text-sm font-normal"
                  htmlFor="unloadCountry"
                >
                  Unload Country <span className="text-red-500">*</span>
                </label>
                <CountrySelectInput
                  name="unloadCountry"
                  id="unloadCountry"
                  placeholder="Select Countries"
                  value={values.unloadCountry}
                  onChange={handleChange}
                />
                {validationErrors.unloadCountry && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.unloadCountry}</p>
                )}
              </div>

              <div className="flex w-full md:w-1/3 gap-3">
                <div className="w-full md:w-1/2">
                  <label
                    htmlFor="airfreightCurrency"
                    className="block mb-1 text-sm font-medium text-gray-700"
                  >
                    Currency <span className="text-red-500">*</span>
                  </label>
                  <Select
                    id="airfreightCurrency"
                    className="w-full text-sm outline-none focus:border-blue-500"
                    value={values.airfreightCurrency}
                    onChange={(value) =>
                      handleSelectChange("airfreightCurrency", value)
                    }
                  >
                    <Option value="USD">USD</Option>
                    <Option value="EUR">EUR</Option>
                    <Option value="GBP">GBP</Option>
                  </Select>
                </div>

                <div className="w-full md:w-1/2">
                  <label
                    htmlFor="afcWeightUnit"
                    className="block mb-1 text-sm font-medium text-gray-700"
                  >
                    Weight Unit <span className="text-red-500">*</span>
                  </label>
                  <Select
                    id="afcWeightUnit"
                    className="w-full text-sm outline-none focus:border-blue-500"
                    value={values.afcWeightUnit}
                    onChange={(value) =>
                      handleSelectChange("afcWeightUnit", value)
                    }
                  >
                    <Option value="kg">kg</Option>
                    <Option value="lbs">lbs</Option>
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <ServiceTempIOR
                formData={values}
                setFormData={setValues}
                setTemplateSelected={setTemplateSelected}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
                savedServices={savedServices}
                setSavedServices={setSavedServices}
              />
            </div>

            <div className="mx-[30px] flex flex-col gap-3 sm:flex-row justify-between mt-4">
              <div className="flex gap-2 items-center">
                <CustomButton
                  label="Update"
                  onClick={handleSubmit}
                  disabled={!isFormValid}
                  className={!isFormValid ? 'opacity-50 cursor-not-allowed' : ''}
                />
                <CustomButton
                  label="Test"
                  onClick={handleTestClick}
                  className="bg-green-500 text-white"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
      <TestPopup
        isVisible={isTestPopupVisible}
        onClose={handleTestPopupClose}
        formData={values}
        key={isTestPopupVisible ? 'open' : 'closed'} // Force re-render on open/close
      />
    </DashboardLayout>
  );
};

export default EditIORForm;